// Which deployment this bundle is running as: "production", "staging"
// or "development".
//
// Why this exists
// ---------------
// Sentry was told `import.meta.env.MODE`, which Vite sets to
// "production" for ANY `vite build` — including the staging build. So
// every staging error arrived in Sentry tagged `environment=production`.
// That is how the 2026-08-05 `AbortError` on /producer-login was
// reported as a production issue while its own URL said
// `staging.produce-app.com`: staging noise sitting in the middle of the
// production alerts you actually need to act on.
//
// Resolution order, most to least trustworthy:
//   1. VITE_APP_ENV — set by the deploy workflow. Named after the
//      mobile app's APP_ENV, which additionally has a "preview" value
//      (apps/mobile/app.config.ts); "preview" is accepted here and
//      resolved to staging, since that is what a preview deploy is.
//   2. The hostname. A transitional fallback with real value: bundles
//      already deployed without the variable still label themselves
//      correctly, so this fixes the mislabelling without waiting for a
//      redeploy. Once every deployed bundle carries VITE_APP_ENV this
//      branch can go — it hardcodes a domain list that would silently
//      call production "staging" if the domain ever changed.
//   3. Vite's MODE — only meaningful for "development".

export type AppEnvironment = "production" | "staging" | "development";

/** Hostnames that are unambiguously production. */
const PRODUCTION_HOSTS = ["produce-app.com", "www.produce-app.com"];

/**
 * @param configured Value of VITE_APP_ENV, if the build set one.
 * @param hostname   window.location.hostname.
 * @param mode       import.meta.env.MODE.
 */
export function resolveAppEnvironment({
  configured,
  hostname,
  mode,
}: {
  configured?: string;
  hostname?: string;
  mode?: string;
}): AppEnvironment {
  const explicit = (configured ?? "").trim().toLowerCase();
  if (explicit === "production" || explicit === "staging" || explicit === "development") {
    return explicit;
  }
  if (explicit === "preview") return "staging";

  const host = (hostname ?? "").trim().toLowerCase();
  if (host) {
    if (host === "localhost" || host === "127.0.0.1" || host.endsWith(".local")) {
      return "development";
    }
    if (PRODUCTION_HOSTS.includes(host)) return "production";
    // Anything else served over a real hostname is a preview or a
    // staging deploy. Guessing "staging" is the safe way round: a
    // staging error wrongly filed under production costs an alert you
    // act on for nothing, the other way round costs a line in a
    // dashboard.
    return "staging";
  }

  return mode === "production" ? "production" : "development";
}
